import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, House, Package, Repeat, Truck, UserPlus } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Banner } from '@shared/ui/feedback/Banner';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import { Stepper } from '@shared/ui/lists/Stepper';
import { toApiError } from '@shared/api/errors';
import { cn } from '@shared/lib/cn';
import { EntryTypeGlyph } from './EntryTypeList';
import { NewPinForm, PersonAvatar, firstName } from './NewPinSheet';
import { PICKER_ROW, PeoplePicker, joinFirstNames } from './PeoplePicker';
import { DetailsForm, type PersonDetails } from './PersonSheet';
import { fetchAccessEntryTypesForInvite, fetchAccessInviteLocations, type MemberAccessEntryTypeOption } from './api';
import { MEMBER_CONTACTS_QUERY_KEY, createMemberContact, fetchMemberContacts, type MemberContact, type Person } from './memberContactsApi';
import type { MemberAppFeatures, VisitorGroup } from './memberAppApi';
import { missingVisitorContact, resolveVisitorContactRule } from './visitorContactRule';
import type { BookingProperty, BookingProps } from './bookings/BookingForm';
import { DeliveryBooking } from './bookings/DeliveryBooking';
import { GoodsRemovalBooking } from './bookings/GoodsRemovalBooking';
import { GuestStayBooking } from './bookings/GuestStayBooking';
import { contactLine, entryTypeRule } from './visitorAccessFormat';
import { ICON_CHIP } from './visitorAccessStyles';

const TYPE_STEP = { label: 'Type', title: 'What is the visit for?', description: 'Pick the kind of visit. Bookings ask for a little more.' };

/** A visitor PIN: the type, who it is for, then the visit. */
const VISITOR_STEPS = [
  TYPE_STEP,
  { label: 'Person', title: 'Who is it for?', description: 'Pick a group, or tick one person or several.' },
  { label: 'Visit', title: 'When is the visit?', description: 'We send them the PIN. You can share it too.' },
];

/** A regular: who, then their days on their own page, where the gate's details are asked too. */
const REGULAR_STEPS = [
  TYPE_STEP,
  { label: 'Person', title: 'Who comes regularly?', description: 'Pick one of your people.' },
  { label: 'Days', title: '', description: '' },
];

/** What the member can book, each offered while some property has it switched on. A regular has no booking steps of its own. */
const BOOKINGS: {
  feature: 'deliveries' | 'guest_stays' | 'goods_removal' | 'regulars';
  label: string;
  rule: string;
  icon: typeof Truck;
  Booking?: (props: BookingProps) => JSX.Element;
}[] = [
  { feature: 'deliveries', label: 'Delivery or collection', rule: 'A courier, any day in a window', icon: Truck, Booking: DeliveryBooking },
  { feature: 'guest_stays', label: 'Guest stay', rule: 'Overnight guests. The gate texts their PIN', icon: House, Booking: GuestStayBooking },
  { feature: 'goods_removal', label: 'Goods removal', rule: 'Someone taking goods out', icon: Package, Booking: GoodsRemovalBooking },
  { feature: 'regulars', label: 'Regular visitor', rule: 'Same days every week, one PIN', icon: Repeat },
];

type Booking = (typeof BOOKINGS)[number];

/** A type, a saved person or Someone new, the way Add a person draws its choices. */
const CHOICE_ROW =
  'flex w-full items-center gap-3 rounded-lg border border-hairline bg-card px-3.5 py-3 text-left';

interface NewPinStepsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once the PINs are made. */
  onDone: () => void;
}

/**
 * New PIN: what the visit is for first, since it decides what is asked next. A visitor type goes on
 * to who it is for and the visit; a booking opens its own steps; a regular opens the person's regular page.
 */
export function NewPinStepsSheet({ open, onOpenChange, onDone }: NewPinStepsSheetProps): JSX.Element {
  const navigate = useNavigate();
  const [type, setType] = useState<MemberAccessEntryTypeOption>();
  const [booking, setBooking] = useState<Booking>();
  // Who is ticked, from the list or a group, and the people the visit is then for.
  const [ticked, setTicked] = useState<string[]>([]);
  const [group, setGroup] = useState<VisitorGroup>();
  const [chosen, setChosen] = useState<Person[]>();
  // People added as someone new in this New PIN, listed first so they are easy to find.
  const [added, setAdded] = useState<string[]>([]);
  const locationsQuery = useQuery({ queryKey: ['access-invite-locations'], queryFn: fetchAccessInviteLocations });

  // 1. Every way out leaves the next open on the first step, once the sheet has slid away.
  const reset = () => {
    setType(undefined);
    setBooking(undefined);
    setTicked([]);
    setGroup(undefined);
    setChosen(undefined);
    setAdded([]);
  };

  // 2. The properties each booking is switched on for.
  const locations = locationsQuery.data ?? [];
  const propertiesFor = (feature: keyof MemberAppFeatures) =>
    locations.filter((location): location is BookingProperty => Boolean(location.features?.[feature]));
  const bookings = BOOKINGS.filter((option) => propertiesFor(option.feature).length > 0);

  // 3. A booking with steps of its own fills the sheet, with Type ahead of them to go back.
  if (booking?.Booking) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} onExitComplete={reset} title={booking.label} height="88dvh">
        <booking.Booking
          properties={propertiesFor(booking.feature)}
          lead={{ label: TYPE_STEP.label, onBack: () => setBooking(undefined) }}
          onCreated={onDone}
        />
      </BottomSheet>
    );
  }

  const steps = booking ? REGULAR_STEPS : VISITOR_STEPS;
  const step = chosen ? 2 : type || booking ? 1 : 0;
  const single = chosen?.length === 1 ? chosen[0] : undefined;
  const title = single ? `New PIN for ${firstName(single.display_name)}` : 'New PIN';

  // 4. Several people share the visit.
  const heading =
    step === 2 && !single
      ? { title: steps[2]!.title, description: `${joinFirstNames(chosen!)} each get their own PIN.` }
      : steps[step]!;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} onExitComplete={reset} title={title}>
      <div className="flex flex-col gap-4.5 pb-4">
        {/* 5. Where they are; an earlier step can be tapped to go back to it. */}
        <Stepper
          total={steps.length}
          current={step}
          labels={steps.map(({ label }) => label)}
          onStepSelect={(index) => {
            setChosen(undefined);
            if (index === 0) {
              setType(undefined);
              setBooking(undefined);
              setTicked([]);
              setGroup(undefined);
            }
          }}
          isStepAccessible={(index) => index < step}
        />
        <div className="flex flex-col gap-1">
          <p className="text-headline font-bold text-ink">{heading.title}</p>
          <p className="text-[0.8125rem] leading-snug text-ink-secondary">{heading.description}</p>
        </div>

        {/* 6. Type, then who, then the visit. A regular goes to their own page once picked. */}
        {step === 0 ? <TypePicker bookings={bookings} onType={setType} onBooking={setBooking} /> : null}
        {step === 1 && type ? (
          <PersonOfTypePicker
            type={type}
            added={added}
            ticked={ticked}
            onTick={setTicked}
            group={group}
            onGroup={setGroup}
            onContinue={setChosen}
            onAdded={(saved) => setAdded([saved.member_contact_id, ...added])}
          />
        ) : null}
        {step === 1 && booking ? (
          <RegularPersonPicker
            onPick={(picked) => {
              onOpenChange(false);
              navigate(`/app/access-invites/people/${picked.member_contact_id}/regular`);
            }}
          />
        ) : null}
        {chosen && type ? (
          <NewPinForm people={chosen} type={type} showPerson onCreated={onDone} />
        ) : null}
      </div>
    </BottomSheet>
  );
}

/** The organisation's visitor types, then the bookings the member's properties allow. */
function TypePicker({
  bookings,
  onType,
  onBooking,
}: {
  bookings: Booking[];
  onType: (type: MemberAccessEntryTypeOption) => void;
  onBooking: (booking: Booking) => void;
}): JSX.Element {
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });

  if (typesQuery.isLoading) return <Loading />;
  if (typesQuery.isError) return <ErrorState onRetry={() => void typesQuery.refetch()} />;

  const types = typesQuery.data ?? [];
  return (
    <div className="flex flex-col gap-2.5">
      {types.length ? <p className="eyebrow">People coming</p> : null}
      {types.map((type) => (
        <button key={type.access_entry_type_id} type="button" onClick={() => onType(type)} className={CHOICE_ROW}>
          <EntryTypeGlyph type={type} />
          <RowText title={type.name} line={entryTypeRule(type)} />
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
        </button>
      ))}

      {bookings.length ? <p className="eyebrow mt-1.5">Bookings</p> : null}
      {bookings.map((option) => (
        <button key={option.feature} type="button" onClick={() => onBooking(option)} className={CHOICE_ROW}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
            <option.icon className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <RowText title={option.label} line={option.rule} />
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
        </button>
      ))}
    </div>
  );
}

/** The member's groups and contacts to tick, anyone on another gate system greyed out, then Someone new as this type. */
function PersonOfTypePicker({
  type,
  added,
  ticked,
  onTick,
  group,
  onGroup,
  onContinue,
  onAdded,
}: {
  type: MemberAccessEntryTypeOption;
  /** People added as someone new in this New PIN, newest first. */
  added: string[];
  ticked: string[];
  onTick: (ids: string[]) => void;
  group?: VisitorGroup;
  onGroup: (group?: VisitorGroup) => void;
  onContinue: (people: Person[]) => void;
  onAdded: (person: Person) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [blocked, setBlocked] = useState<{ person: Person; reason: string }>();
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });

  // 1. Someone new is saved as this type in their own drawer, then shows ticked on the list.
  const mutation = useMutation({
    mutationFn: (details: PersonDetails) => createMemberContact({ ...details, access_entry_type_id: type.access_entry_type_id }),
    onSuccess: (saved) => {
      queryClient.setQueryData<MemberContact[]>(MEMBER_CONTACTS_QUERY_KEY, (people) => [
        ...(people ?? []),
        { ...saved, live_invites: [], last_arrived_at: null, last_pin_at: null },
      ]);
      void queryClient.invalidateQueries({ queryKey: MEMBER_CONTACTS_QUERY_KEY });
      onAdded(saved);
      setBlocked(undefined);
      onTick(type.group_size ? ticked.concat(saved.member_contact_id) : [saved.member_contact_id]);
      setAdding(false);
      toast.success(`${saved.display_name} added`);
    },
    onError: (error) => toast.danger("Couldn't save", toApiError(error).message),
  });

  // 2. The contact the type sends the PIN on; someone without it can't be ticked.
  const rule = resolveVisitorContactRule(type, type.capture_requirements);
  const blockedReason = (person: Person) => {
    const missing = missingVisitorContact(rule, person.phone ?? '', person.email ?? '');
    if (missing.mobile) return rule.eitherRequired ? 'No mobile or email saved' : 'No mobile saved';
    return missing.email ? 'No email saved' : undefined;
  };

  // 3. Anyone of Any type can get this PIN. Anyone else needs their own type still offered (it drops
  //    out when its connection is switched off) and opening the gate through the same system.
  const allowed = (person: Person) => {
    if (!person.access_entry_type_id) return true;
    const theirs = typesQuery.data?.find((option) => option.access_entry_type_id === person.access_entry_type_id);
    if (!theirs) return false;
    return !theirs.gate_system || !type.gate_system || theirs.gate_system === type.gate_system;
  };

  return (
    <>
      {blocked ? (
        <Banner variant="warning" title={blocked.reason} description={`Add it to ${blocked.person.display_name}'s details under Contacts to make their PIN.`} />
      ) : null}

      {/* 4. Groups, then contacts, the ones just added first, then Someone new. */}
      <PeoplePicker
        allowed={allowed}
        first={added}
        blockedReason={blockedReason}
        groupSize={type.group_size}
        typeName={type.name}
        ticked={ticked}
        onTick={(ids) => {
          setBlocked(undefined);
          onTick(ids);
        }}
        group={group}
        onGroup={onGroup}
        onBlocked={(person) => setBlocked({ person, reason: blockedReason(person)! })}
        onContinue={onContinue}
        emptyText="No contacts yet. Add them as someone new."
      >
        <button type="button" onClick={() => setAdding(true)} className={cn(PICKER_ROW, 'border-dashed text-primary-500')}>
          <span className={ICON_CHIP}>
            <UserPlus className="h-4.5 w-4.5" strokeWidth={2} />
          </span>
          <span className="text-[15px] font-semibold">Someone new</span>
        </button>
      </PeoplePicker>

      {/* 5. The Someone new drawer: just the fields the type asks for, then Cancel or Add. */}
      <BottomSheet
        open={adding}
        onOpenChange={setAdding}
        title="Someone new"
        titleAccessory={
          <button
            type="button"
            aria-label="Close"
            // Stop the tap bubbling to the header's drag handler (would swallow the click).
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setAdding(false)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-sunken text-ink"
          >
            <ChevronDown className="h-5 w-5" strokeWidth={2} />
          </button>
        }
      >
        <div className="pb-4">
          <DetailsForm
            type={type}
            submitLabel="Add"
            saving={mutation.isPending}
            onSubmit={(details) => mutation.mutate(details)}
            onCancel={() => setAdding(false)}
          />
        </div>
      </BottomSheet>
    </>
  );
}

/** Any of the member's people can be a regular, whatever their type. */
function RegularPersonPicker({ onPick }: { onPick: (person: Person) => void }): JSX.Element {
  const peopleQuery = useQuery({ queryKey: MEMBER_CONTACTS_QUERY_KEY, queryFn: fetchMemberContacts });
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });

  if (peopleQuery.isLoading || typesQuery.isLoading) return <Loading />;
  if (peopleQuery.isError || typesQuery.isError)
    return <ErrorState onRetry={() => void Promise.all([peopleQuery.refetch(), typesQuery.refetch()])} />;

  const people = (peopleQuery.data ?? []).filter((person) => person.is_active);

  return (
    <div className="flex flex-col gap-2.5">
      {people.length ? (
        people.map((person) => (
          <button key={person.member_contact_id} type="button" onClick={() => onPick(person)} className={CHOICE_ROW}>
            <PersonAvatar name={person.display_name} />
            <RowText title={person.display_name} line={contactLine(person, typesQuery.data)} />
            <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
          </button>
        ))
      ) : (
        <p className="text-[0.8125rem] text-ink-secondary">Add the person first under Contacts, then make them a regular.</p>
      )}
    </div>
  );
}

function RowText({ title, line }: { title: string; line: string }): JSX.Element {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
      <span className="truncate text-[0.9375rem] font-semibold text-ink">{title}</span>
      <span className="text-caption text-ink-muted">{line}</span>
    </span>
  );
}

function Loading(): JSX.Element {
  return (
    <>
      <Skeleton className="h-[4.25rem] rounded-lg" />
      <Skeleton className="h-[4.25rem] rounded-lg" />
    </>
  );
}
